import { http } from '@/api/core/request';
import { ApiUrls } from '@/api/endpoints/apiUrls';
import {
  CreateGroupParams,
  UpdateGroupParams,
  GroupInfo,
  AddGroupParticipantsParams,
  RemoveGroupParticipantsParams,
  GroupMember,
  GroupMembersResult,
  GroupListResult,
  SearchParams,
  SetGroupAnnouncementParams,
} from '@/api/types';

/**
 * 群组服务
 * 处理群组相关的API请求
 */
export class GroupService {
  /**
   * 创建群组
   * @param params 创建群组参数
   * @returns 空响应
   */
  static async createGroup(params: CreateGroupParams): Promise<GroupInfo> {
    return http.post<GroupInfo>(ApiUrls.GROUP.CREATE, params);
  }

  /**
   * 更新群组信息
   * @param groupId 群组ID
   * @param params 更新参数
   * @returns 空响应
   */
  static async updateGroup(groupId: string, params: UpdateGroupParams): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.UPDATE, { group_id: groupId });
    return http.post<void>(url, params);
  }

  /**
   * 获取群组信息
   * @param groupId 群组ID
   * @returns 群组信息
   */
  static async getGroupInfo(groupId: string): Promise<GroupInfo> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.INFO, { group_id: groupId });
    return http.get<GroupInfo>(url);
  }

  /**
   * 离开群组
   * @param groupId 群组ID
   * @returns 空响应
   */
  static async leaveGroup(groupId: string): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.LEAVE, { group_id: groupId });
    return http.post<void>(url);
  }

  /**
   * 移除群组成员
   * @param groupId 群组ID
   * @param params 移除成员参数
   * @returns 空响应
   */
  static async removeParticipants(groupId: string, params: RemoveGroupParticipantsParams): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.REMOVE_PARTICIPANTS, { group_id: groupId });
    return http.post<void>(url, params);
  }

  /**
   * 添加群组成员
   * @param groupId 群组ID
   * @param params 添加成员参数
   * @returns 空响应
   */
  static async addParticipants(groupId: string, params: AddGroupParticipantsParams): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.ADD_PARTICIPANTS, { group_id: groupId });
    return http.post<void>(url, params);
  }

  /**
   * 获取群组成员列表（所有成员）
   * @param groupId 群组ID
   * @param params 搜索参数
   * @returns 成员列表
   */
  static async getParticipantList(groupId: string, params?: SearchParams): Promise<GroupMember[]> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.PARTICIPANT_LIST, { group_id: groupId });
    return http.get<GroupMember[]>(url, params);
  }

  /**
   * 获取群组成员列表（分页）
   * @param groupId 群组ID
   * @param params 搜索和分页参数
   * @returns 分页成员列表
   */
  static async getParticipantListPaginated(groupId: string, params?: SearchParams): Promise<GroupMembersResult> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.PARTICIPANT_LIST_PAGINATED, { group_id: groupId });
    return http.get<GroupMembersResult>(url, params);
  }

  /**
   * 获取用户群组列表
   * @returns 群组列表
   */
  static async getGroupList(): Promise<GroupListResult> {
    return http.get<GroupListResult>(ApiUrls.GROUP.LIST);
  }

  /**
   * 解散群组
   * @param groupId 群组ID
   * @returns 空响应
   */
  static async disbandGroup(groupId: string): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.DISBAND, { group_id: groupId });
    return http.post<void>(url);
  }

  /**
   * 设置群组公告
   * @param groupId 群组ID
   * @param params 公告内容参数
   * @returns 空响应
   */
  static async setGroupAnnouncement(groupId: string, params: SetGroupAnnouncementParams): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.SET_ANNOUNCEMENT, { group_id: groupId });
    return http.post<void>(url, params);
  }

  /**
   * 删除群组公告
   * @param groupId 群组ID
   * @returns 空响应
   */
  static async removeGroupAnnouncement(groupId: string): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.GROUP.REMOVE_ANNOUNCEMENT, { group_id: groupId });
    return http.delete<void>(url);
  }

}
